/**
 * Model Catalog
 *
 * Single source of truth for all AI models — capabilities, pricing, and billing.
 *
 * Data lives in models.json (one entry per model).
 * Purpose: factory defaults for DB seeding and reset operations.
 * At runtime, ALL calls go through the DB-backed cache populated on startup.
 * The static catalog is only used when the DB is unreachable.
 */

import { z } from "zod";

import { DbEnum } from "../generated/prisma-enums";
import { ModelCapabilitySchema } from "./model-capability";
import { retirementFallback } from "./model-fallbacks";
import modelsJson from "./models.json";
import {
  ANTHROPIC_ADAPTIVE_LEVELS,
  ANTHROPIC_EFFORT_LEVELS,
  ANTHROPIC_LEGACY_LEVELS,
  ANTHROPIC_THINKING_DIALECTS,
  type AnthropicAdaptiveLevel,
  type AnthropicEffortLevel,
  type AnthropicLegacyLevel,
  type AnthropicThinkingDialect,
  AnthropicThinkingDialectSchema,
  type AnthropicThinkingDisplay,
  DEFAULT_EFFORTS,
  type DefaultEffort,
  DefaultEffortSchema,
  EFFORT_LADDER,
  EFFORT_OFF_FORMS,
  type EffortOffForm,
  EffortOffFormSchema,
  type EffortRung,
  encodeTuning,
  GEMINI_BUDGET_LEVELS,
  GEMINI_LEVEL_LEVELS,
  type GeminiBudgetLevel,
  type GeminiLevelLevel,
  type GeminiWireLevel,
  getDialectLevels,
  getDialectSpec,
  KIMI_EFFORT_LEVELS,
  type KimiEffortLevel,
  LEGACY_WORD_RUNGS,
  OFF_FORMS_BY_DIALECT,
  offFormsOf,
  OPENAI_EFFORT_LEVELS,
  type OpenAIEffortLevel,
  parseThinkingDialect,
  type ResolvedEffort,
  type ResolvedTuning,
  rungOf,
  TEMPERATURE_RULES,
  type TemperatureRule,
  TemperatureRuleSchema,
  type ThinkingDialect,
  ThinkingDialectSchema,
  type TuningParams
} from "./thinking-dialect.registry";

export {
  ANTHROPIC_ADAPTIVE_LEVELS,
  ANTHROPIC_EFFORT_LEVELS,
  ANTHROPIC_LEGACY_LEVELS,
  ANTHROPIC_THINKING_DIALECTS,
  AnthropicThinkingDialectSchema,
  DEFAULT_EFFORTS,
  DefaultEffortSchema,
  EFFORT_LADDER,
  EFFORT_OFF_FORMS,
  EffortOffFormSchema,
  encodeTuning,
  GEMINI_BUDGET_LEVELS,
  GEMINI_LEVEL_LEVELS,
  getDialectLevels,
  getDialectSpec,
  KIMI_EFFORT_LEVELS,
  LEGACY_WORD_RUNGS,
  OFF_FORMS_BY_DIALECT,
  OPENAI_EFFORT_LEVELS,
  parseThinkingDialect,
  rungOf,
  TEMPERATURE_RULES,
  TemperatureRuleSchema,
  ThinkingDialectSchema
};
export type {
  AnthropicAdaptiveLevel,
  AnthropicEffortLevel,
  AnthropicLegacyLevel,
  AnthropicThinkingDialect,
  AnthropicThinkingDisplay,
  DefaultEffort,
  EffortOffForm,
  EffortRung,
  GeminiBudgetLevel,
  GeminiLevelLevel,
  GeminiWireLevel,
  KimiEffortLevel,
  OpenAIEffortLevel,
  ResolvedEffort,
  ResolvedTuning,
  TemperatureRule,
  ThinkingDialect,
  TuningParams
};

// =============================================================================
// Thinking / reasoning level vocabularies (per-dialect, sourced from registry)
// =============================================================================

/**
 * Combined Gemini level vocabulary covering both gemini-budget and gemini-level dialects.
 * Used by ModelConfigSchema to accept any historical user choice; the dialect-keyed
 * builder narrows to the correct shape at inference.
 */
/**
 * Spread both dialect level sets, filtering out duplicates already covered by
 * the budget set. The filter-based dedup preserves the `[T, ...T[]]` tuple type
 * required by Zod (a Set-based dedup would erase it). Either source set can
 * grow new values without breaking this — added budget values come through
 * the spread, added level values come through the filter, and any overlap
 * is dropped.
 */
export const GEMINI_EFFORT_LEVELS = [
  ...GEMINI_BUDGET_LEVELS,
  ...GEMINI_LEVEL_LEVELS.filter(
    (lvl): lvl is Exclude<GeminiLevelLevel, GeminiBudgetLevel> =>
      !(GEMINI_BUDGET_LEVELS as readonly string[]).includes(lvl)
  )
] as const;
export type GeminiEffortLevel = (typeof GEMINI_EFFORT_LEVELS)[number];

/**
 * Every level value any dialect accepts, each appearing ONCE.
 *
 * Deduped for the same reason `GEMINI_EFFORT_LEVELS` and
 * `ANTHROPIC_EFFORT_LEVELS` are: the CLI's `gen:contract-help` derives a flag's
 * `--choices()` by reading the published contract, so a duplicated member is
 * offered to the operator twice. `ModelConfigBaseShape.reasoningLevel` is a
 * `z.enum` over this, and the five source sets overlap heavily — `low`, `medium`
 * and `high` are in four of them — so the raw spread published 21 values for 10
 * distinct levels.
 *
 * Filter-based rather than `Set`-based because `z.enum` requires the
 * `[T, ...T[]]` tuple type, which a `Set` round-trip erases.
 */
const EFFORT_LEVEL_SOURCES = [
  ...OPENAI_EFFORT_LEVELS,
  ...ANTHROPIC_LEGACY_LEVELS,
  ...ANTHROPIC_ADAPTIVE_LEVELS,
  ...GEMINI_EFFORT_LEVELS,
  ...KIMI_EFFORT_LEVELS
] as const;

export const ALL_EFFORT_LEVELS = [
  EFFORT_LEVEL_SOURCES[0],
  ...EFFORT_LEVEL_SOURCES.filter(
    (level, index): boolean => index > 0 && EFFORT_LEVEL_SOURCES.indexOf(level) === index
  )
] as const;
export type AllEffortLevel = (typeof EFFORT_LEVEL_SOURCES)[number];

// =============================================================================
// Schema + types
// =============================================================================

/**
 * A catalog-entry pricing fact: past `thresholdTokens` input tokens in ONE
 * request, every rate of that request is multiplied — `inputMultiplier` and
 * `outputMultiplier` for input and output, `cacheMultiplier` for every cache
 * term (read and write).
 */
export const LongContextPricingSchema = z.object({
  thresholdTokens: z.number().int().positive(),
  inputMultiplier: z.number().positive(),
  cacheMultiplier: z.number().positive(),
  outputMultiplier: z.number().positive()
});
export type LongContextPricing = z.infer<typeof LongContextPricingSchema>;

/**
 * Base Zod object — exposes `.shape` for field-level access.
 * Use `ModelCatalogEntrySchema` (the refined version below) for full catalog entry validation.
 *
 * 🔴 `.strict()`, SO AN UNDECLARED KEY IS A REFUSAL AND NOT A SILENT DROP. A plain
 * `z.object` VALIDATES an entry carrying a retired capability boolean — measured:
 * `supportsThinking: true` parses with `success: true` and the key is then absent from
 * `result.data` — which is the same class as a `safeParse` with a null fallback: it
 * reports success and destroys the value.
 *
 * ⚠️ THE REFUSAL FIRES AT MODULE LOAD, because `MODEL_CATALOG` below is
 * `ModelCatalogEntrySchema.parse(raw)` at import time. So a typo'd key in `models.json`
 * stops every consumer's import rather than failing one test. That direction is chosen:
 * `models.json` is hand-maintained in this repo, and a key nothing reads is already a
 * silent bug — loud, ours, at parse time.
 *
 * It belongs HERE rather than on the refined schema below, which is a derived view: this
 * is the object other modules read `.shape` off, so strictness sits where the shape is
 * declared. `.refine()` returns a `ZodEffects`, which has no `.strict()` to call.
 *
 * A declared-but-unused optional key (`apiType`) is unaffected — this constrains UNKNOWN
 * keys, never unused ones.
 */
export const ModelCatalogEntryObjectSchema = z
  .object({
    provider: z.enum(DbEnum.MODEL_PROVIDER_VALUES),
    displayName: z.string(),
    /** Short brand name for compact UI labels (e.g. "Sonnet 4.5") */
    modelDisplayName: z.string().optional(),
    /** API model name sent to the provider (may differ from catalog key for code interpreter models) */
    modelName: z.string(),

    // Pricing (USD per 1M tokens)
    inputTokenPrice: z.number(),
    outputTokenPrice: z.number(),
    cacheReadTokenPrice: z.number().optional(),
    cacheWriteTokenPrice: z.number().optional(),
    /** See {@link LongContextPricingSchema}. Absent means no long-context surcharge. */
    longContextPricing: LongContextPricingSchema.optional(),

    // Capabilities
    /**
     * The kind of call this model answers — `generate` or `classify`. Absent means
     * `generate`; read it through `modelCapabilityOf`, never by comparing the
     * provider. See `model-capability.ts`.
     *
     * Static-catalog only, like `supportedReasoningLevels`: the `ai_model` table
     * has no column for it, so the runtime cache carries it across from here by
     * model id.
     */
    capability: ModelCapabilitySchema.optional(),
    contextSize: z.number().optional(),
    streaming: z.boolean(),

    // Agent runtime
    systemRole: z.enum(["system", "developer", "user"]).optional(),
    codeInterpreter: z.boolean(),
    /** API model name sent to the Anthropic API (may differ from catalog key for code-interpreter models) */
    underlyingModel: z.string().optional(),
    /**
     * Model ID accepted by the Claude Code SDK.
     * Only set when the SDK allowlist differs from the Anthropic API model name.
     * At inference: claudeCodeModelId ?? underlyingModel ?? modelName
     */
    claudeCodeModelId: z.string().optional(),
    fastMode: z.boolean(),

    /**
     * Thinking / reasoning dialect.
     *
     * Absent → the model does not support any form of thinking. Present → the
     * dialect determines the user-facing levels, default level, and how
     * (level, dialect) is translated to the provider request shape. See
     * `thinking-dialect.registry.ts` for the full taxonomy and dispatch.
     */
    thinkingDialect: ThinkingDialectSchema.optional(),
    /**
     * Provider capability ceiling — the max output tokens the provider API accepts for this
     * model. Sent as the default `max_tokens` when no consumer budget is set, and the upper
     * clamp for any consumer budget. Required for every catalog entry; validated against all
     * models.json entries at build time.
     */
    maxOutputTokens: z.number().int().positive(),

    apiType: z.enum(["completions", "responses"]).optional(),

    /**
     * Per-model reasoning-level subset. Meaningful for openai-reasoning, gemini-level,
     * and gemini-budget dialects. When absent, the dialect's full level set applies.
     * In-memory only — not persisted to DB. Read from MODEL_CATALOG, not runtime cache.
     */
    supportedReasoningLevels: z.array(z.enum(ALL_EFFORT_LEVELS)).optional(),

    defaultEffort: DefaultEffortSchema,
    effortOff: EffortOffFormSchema.optional(),
    temperatureRule: TemperatureRuleSchema
  })
  .strict();

/**
 * Per-dialect alphabet — full set of values each dialect accepts.
 * Used by the catalog refine to validate per-model subsets.
 */
const DIALECT_ALPHABETS: Record<ThinkingDialect, readonly string[]> = {
  "anthropic-legacy": ANTHROPIC_LEGACY_LEVELS,
  "anthropic-adaptive": ANTHROPIC_ADAPTIVE_LEVELS,
  "openai-reasoning": OPENAI_EFFORT_LEVELS,
  "gemini-level": GEMINI_LEVEL_LEVELS,
  "gemini-budget": GEMINI_BUDGET_LEVELS,
  "kimi-effort": KIMI_EFFORT_LEVELS
};

type CatalogEntryInput = z.infer<typeof ModelCatalogEntryObjectSchema>;

/**
 * The levels one entry's `supportedReasoningLevels` may draw from — its dialect's
 * alphabet, narrowed by transport where the API it speaks accepts less: OpenAI's
 * Chat Completions API rejects `max`, which its Responses API accepts.
 */
function entryAlphabet(entry: CatalogEntryInput): readonly string[] {
  if (!entry.thinkingDialect) return [];
  const alphabet = DIALECT_ALPHABETS[entry.thinkingDialect];
  if (entry.thinkingDialect === "openai-reasoning" && entry.apiType === "completions") {
    return alphabet.filter((lvl) => lvl !== "max");
  }
  return alphabet;
}

function isOffFormOfItsDialect(entry: CatalogEntryInput): boolean {
  if (entry.effortOff === undefined) return true;
  return offFormsOf(entry.thinkingDialect ?? null).includes(entry.effortOff);
}

function offeredRungsOf(entry: CatalogEntryInput): readonly EffortRung[] {
  return (entry.supportedReasoningLevels ?? []).map(rungOf);
}

function isDefaultEffortOffered(entry: CatalogEntryInput): boolean {
  switch (entry.defaultEffort) {
    case "vendor-default":
      return true;
    case "none":
      return entry.effortOff !== undefined;
    default:
      return offeredRungsOf(entry).includes(entry.defaultEffort);
  }
}

function isOffFormAnOfferedLevel(entry: CatalogEntryInput): boolean {
  if (entry.effortOff === undefined) return false;
  return (entry.supportedReasoningLevels ?? []).some((level) => level === entry.effortOff);
}

export const ModelCatalogEntrySchema = ModelCatalogEntryObjectSchema.refine(
  (entry) => {
    if (!entry.thinkingDialect) {
      return !entry.supportedReasoningLevels || entry.supportedReasoningLevels.length === 0;
    }
    return (
      Array.isArray(entry.supportedReasoningLevels) && entry.supportedReasoningLevels.length > 0
    );
  },
  {
    error: (issue) => {
      const entry = issue.input as CatalogEntryInput;
      return entry.thinkingDialect
        ? `supportedReasoningLevels must be a non-empty array for ${entry.thinkingDialect} dialect`
        : "supportedReasoningLevels must be absent or empty for models without a thinking dialect";
    }
  }
)
  .refine(
    (entry) => {
      if (!entry.thinkingDialect || !entry.supportedReasoningLevels) return true;
      const alphabet = entryAlphabet(entry);
      return entry.supportedReasoningLevels.every((lvl) => alphabet.includes(lvl));
    },
    {
      error: (issue) => {
        const entry = issue.input as CatalogEntryInput;
        const alphabet = entryAlphabet(entry);
        const invalid =
          entry.supportedReasoningLevels?.filter((lvl) => !alphabet.includes(lvl)) ?? [];
        const transport = entry.apiType ? ` over the ${entry.apiType} API` : "";
        return `supportedReasoningLevels contains values not in the ${entry.thinkingDialect} alphabet${transport} [${alphabet.join(", ")}]: ${invalid.join(", ")}`;
      }
    }
  )
  .refine((entry) => isOffFormOfItsDialect(entry), {
    error: (issue) => {
      const entry = issue.input as CatalogEntryInput;
      const allowed = offFormsOf(entry.thinkingDialect ?? null);
      const owner = entry.thinkingDialect ?? "a model with no thinking dialect";
      return `effortOff "${entry.effortOff}" is not an off form of ${owner} [${allowed.join(", ")}]`;
    }
  })
  .refine((entry) => isDefaultEffortOffered(entry), {
    error: (issue) => {
      const entry = issue.input as CatalogEntryInput;
      return `defaultEffort "${entry.defaultEffort}" is not offered by this entry: offered rungs [${offeredRungsOf(entry).join(", ")}], none ${entry.effortOff === undefined ? "not offered (no effortOff)" : "offered"}`;
    }
  })
  .refine((entry) => !isOffFormAnOfferedLevel(entry), {
    error: (issue) => {
      const entry = issue.input as CatalogEntryInput;
      return `effortOff "${entry.effortOff}" is also an offered level of ${entry.thinkingDialect}: offering both sends one request under two options`;
    }
  });
export type ModelCatalogEntry = z.infer<typeof ModelCatalogEntrySchema>;

export function declaredDefaultLevelOf(
  entry: Pick<ModelCatalogEntry, "defaultEffort" | "supportedReasoningLevels">
): AllEffortLevel | undefined {
  if (entry.defaultEffort === "none" || entry.defaultEffort === "vendor-default") return undefined;
  return entry.supportedReasoningLevels?.find((level) => rungOf(level) === entry.defaultEffort);
}

// =============================================================================
// Temperature + context constants
// =============================================================================

export const DEFAULT_TEMPERATURE = 0.7;
export const TEMPERATURE_MIN = 0;
export const TEMPERATURE_MAX = 1;

/** Default context window size (tokens) used when a model has no explicit contextSize. */
export const DEFAULT_CONTEXT_SIZE = 128_000;

// =============================================================================
// The catalog — loaded from models.json, validated at module load
// =============================================================================

export const MODEL_CATALOG: ReadonlyMap<string, ModelCatalogEntry> = new Map(
  Object.entries(modelsJson).map(([id, raw]) => [id, ModelCatalogEntrySchema.parse(raw)])
);

// =============================================================================
// Runtime cache — populated from DB on startup, refreshed after every mutation
// =============================================================================

let _modelRuntimeCache = new Map<string, ModelCatalogEntry>();

/** Backend-only. Called by AiModelPricingAdapter after loading/refreshing from DB. */
export function _setModelRuntimeCache(cache: Map<string, ModelCatalogEntry>): void {
  _modelRuntimeCache = cache;
}

// =============================================================================
// Lookup functions
// =============================================================================

/**
 * Resolves model config. DB cache → JSON catalog → null if not found.
 *
 * Returns null if the model name is unknown to both the DB cache and the static catalog.
 * Callers must handle null explicitly — there is no silent wrong-model fallback.
 */
export function getModel(name: string): ModelCatalogEntry | null {
  if (_modelRuntimeCache.size > 0) {
    return _modelRuntimeCache.get(name) ?? null;
  }
  return MODEL_CATALOG.get(name) ?? null;
}

// =============================================================================
// Wire name — the one derivation of "what identifier does the vendor see"
// =============================================================================

/**
 * The WIRE NAME of a catalog entry: the identifier sent to the vendor's API.
 *
 * Differs from the model id only where an entry WRAPS another model — a
 * code-interpreter entry wraps the real Claude model underneath it — and
 * `underlyingModel` is that wrapped identity. Every other entry's wire name is
 * its `modelName`, which is its model id.
 *
 * **Every surface that addresses a vendor must call this rather than reading
 * `modelName`.** The two spellings are indistinguishable for most of the
 * catalog entries, so a site that reads the model id passes every smoke test
 * and then builds `anthropic/code-interpreter-claude-opus-5` — a name Anthropic
 * does not serve — for the entries that wrap. That is not a 404 anywhere it reaches
 * `resolveInferenceModel`, which substitutes a retirement fallback and sets a
 * flag no caller reads; it IS a hard vendor error on any path that addresses
 * the API directly, which is what browser automation does.
 *
 * `claudeCodeModelId` is deliberately NOT consulted here. It names the id the
 * Claude Code SDK's allowlist accepts, which is a different consumer from the
 * Anthropic API — a caller that needs it composes
 * `claudeCodeModelId ?? wireNameOf(entry)` itself.
 */
export function wireNameOf(
  entry: Pick<ModelCatalogEntry, "modelName" | "underlyingModel">
): string {
  return entry.underlyingModel ?? entry.modelName;
}

// =============================================================================
// Inference model resolution — always lands on a model that exists
// =============================================================================

export type { DecisionAnswer, DecisionAnswers } from "./decision/decision-answer.types";
export {
  aiTaskKindOrGenerative,
  type AITaskKindValue,
  type DecisionTaskFitInput,
  decisionTaskRefusals,
  KIND_CAPABILITY,
  KIND_MODEL_REFUSAL_REASON,
  TASK_REFUSAL_FIELD
} from "./decision/decision-task-refusals";
export {
  JEV_CHOICE_MAX_OPTIONS,
  JEV_SCORE_MAX_LEVELS,
  JEV_SCORE_MIN_LEVELS,
  type JevChoiceQuestion,
  type JevJsonValue,
  type JevNoulQuestion,
  type JevQuestion,
  type JevScoreQuestion
} from "./decision/jev-question.types";
export type {
  JevFieldPlan,
  JevSchemaRefusal,
  JevTranslation
} from "./decision/jev-schema-plan.types";
export { numberScaleLabels, X_CRITERIA_KEYWORD } from "./decision/translate-jev-leaf";
export { translateSchemaToJevQuestions } from "./decision/translate-schema-to-jev-questions";
export {
  DEFAULT_MODEL_CAPABILITY,
  MODEL_CAPABILITIES,
  type ModelCapability,
  modelCapabilityOf,
  ModelCapabilitySchema
} from "./model-capability";
export {
  OVERLOAD_FALLBACKS,
  type OverloadFallback,
  overloadFallbackOf,
  overloadFallbackTuningOf,
  retirementFallback,
  type UnresolvedRetirementFallback,
  unresolvedRetirementFallbacks
} from "./model-fallbacks";
export { isModelProvider } from "./model-provider.guard";

export interface InferenceModelResolution {
  /** Actual API model name to send to the provider */
  wireName: string;
  /** True when we had to substitute a fallback */
  isFallback: boolean;
  /** The original modelId that was requested */
  originalModelId: string;
  /**
   * Catalog entry for the resolved model (original if found, fallback if not).
   * Use this for ALL capability checks (streaming, apiType, fastMode, underlyingModel, etc.)
   * after resolution — never call getModel(originalModelId) again, as deprecated models
   * are excluded from the runtime cache and will return null.
   */
  catalogEntry: ModelCatalogEntry | null;
  /**
   * The catalog KEY this resolved to — the original when the catalog carries it,
   * the provider's retirement fallback when it does not.
   *
   * Distinct from `wireName`, which is the name sent to the vendor and
   * differs wherever an entry wraps another model. Pricing and billing key by
   * the catalog id, so this is the one they must use: billing the stored id
   * charges a model that never ran, finds no pricing, and silently bills zero.
   */
  resolvedModelId: string;
}

/**
 * Resolves the API model name for inference. NEVER returns null or an unknown string.
 *
 * Resolution order:
 * 1. Catalog entry found → use entry.underlyingModel ?? entry.modelName (code-interpreter models
 *    use underlyingModel as the real API name; regular models have modelName == API name)
 * 2. Not found → the provider's retirement fallback
 *
 * Always call this instead of `getModel(x)?.modelName ?? x`.
 * When isFallback is true, log a warning so the team knows to fix the stored modelId.
 *
 * @param modelId  - The modelId stored in agent/task modelConfig
 * @param provider - A model provider name; one outside the set resolves through the
 *                   unrecognized-provider path rather than throwing
 */
export function resolveInferenceModel(modelId: string, provider: string): InferenceModelResolution {
  const entry = getModel(modelId);
  if (entry) {
    return {
      wireName: wireNameOf(entry),
      isFallback: false,
      originalModelId: modelId,
      catalogEntry: entry,
      resolvedModelId: modelId
    };
  }

  const fallbackId = retirementFallback(provider);
  const fallbackEntry = getModel(fallbackId);

  return {
    wireName: fallbackEntry ? wireNameOf(fallbackEntry) : fallbackId,
    isFallback: true,
    originalModelId: modelId,
    catalogEntry: fallbackEntry ?? null,
    resolvedModelId: fallbackId
  };
}

/**
 * The resolution for a model id that is the CUSTOMER'S and not ours — a BYOM
 * custom endpoint. The id resolves to ITSELF: nothing is looked up, nothing is
 * substituted, and no catalog entry is attached.
 *
 * 🔴 WHY THE CATALOG IS NOT CONSULTED AT ALL, WHICH IS THE WHOLE POINT. A custom
 * endpoint's model id belongs to the customer and is never in our catalog, so
 * `resolveInferenceModel` does not report a miss — it SUBSTITUTES the provider's
 * retirement fallback and sets `isFallback`, a flag the calling sites did not
 * read. The request then goes to the customer's base URL, with the customer's key
 * correctly on it, carrying OUR model id. Every turn. It either succeeds against a
 * model the customer never configured or 404s with a name they have never heard
 * of, and nothing logs it as an error, because nothing failed.
 *
 * Looking the id up "in case the catalog happens to carry it" is the same defect
 * wearing a smaller hat: a customer serving their own `gpt-4o` would inherit OUR
 * entry's capabilities — its `apiType`, its `streaming`, its `thinkingDialect` —
 * and we would decide the transport for an endpoint that declared none of it, and
 * send `reasoning_effort` to a model that never asked for it. So `catalogEntry` is
 * `null` unconditionally. That is the honest answer for an endpoint we know
 * nothing about, and every consumer already handles it: capability reads fall back
 * to their defaults, and the customer's own declaration is the only thing entitled
 * to override one.
 *
 * `resolvedModelId` is the customer's id for the reason its own doc comment gives
 * on the type: pricing and billing key off the catalog id, so billing a custom
 * endpoint under a platform id charges a model that never ran.
 *
 * ⚠️ THE PRECONDITION IS THE CALLER'S TO ESTABLISH, AND IT IS THE MIRROR DEFECT.
 * Call this only when the request is genuinely going to the customer's endpoint,
 * and derive that fact from the same value that chose the client rather than
 * passing it independently — the two cannot then disagree. Called on a platform
 * turn, this sends an unresolved, possibly retired id to OUR host.
 *
 * @param modelId - the customer's own model id, exactly as stored
 */
export function resolveInferenceModelVerbatim(modelId: string): InferenceModelResolution {
  return {
    wireName: modelId,
    isFallback: false,
    originalModelId: modelId,
    catalogEntry: null,
    resolvedModelId: modelId
  };
}

// =============================================================================
// Catalog helpers (used by seed service and reset use cases)
// =============================================================================

export function getAllCatalogEntries(): Array<{ modelId: string } & ModelCatalogEntry> {
  return Array.from(MODEL_CATALOG.entries()).map(([modelId, entry]) => ({ modelId, ...entry }));
}

// =============================================================================
// Billing types — provider-specific token usage (discriminated union)
// =============================================================================

interface BaseTokenUsage {
  /** Total input tokens for display/DB (OpenAI: includes cached; Anthropic: non-cache only) */
  promptTokens: number;
  /** Output tokens generated by the model */
  completionTokens: number;
}

export interface OpenAITokenUsage extends BaseTokenUsage {
  provider: "openai";
  /** Tokens served from OpenAI's automatic prompt cache (subset of promptTokens, discounted) */
  cachedTokens: number;
  /**
   * Tokens written to OpenAI's prompt cache during this turn — a subset of
   * `promptTokens`, disjoint from `cachedTokens` (OpenAI's own worked example:
   * `ordinaryInputTokens = promptTokens - cachedTokens - cacheWriteTokens`).
   * Billed at the cache-write rate.
   *
   * A required key whose value may be `undefined`, so a producer that forgets
   * to read it fails typecheck rather than silently reporting no writes.
   * `undefined` means the vendor reported none — Kimi and JEV share this usage
   * shape and never report a write count, and OpenAI itself omits the field on
   * a call that touched no cache. That is not a measured zero: a stored `0`
   * would assert the vendor counted zero writes, a claim it never made.
   */
  cacheWriteTokens: number | undefined;
}

export interface AnthropicTokenUsage extends BaseTokenUsage {
  provider: "anthropic";
  /** Tokens read from Anthropic's prompt cache (priced at the model's cacheReadTokenPrice) */
  cacheReadInputTokens: number;
  /** Tokens written to Anthropic's prompt cache (priced at the model's 5-minute cacheWriteTokenPrice) */
  cacheCreationInputTokens: number;
}

export interface GoogleTokenUsage extends BaseTokenUsage {
  provider: "google";
  /** Tokens served from Google's cache (subset of promptTokens). */
  cachedTokens: number;
}

/**
 * Anthropic Agent SDK usage (Code Interpreter / Daytona sandbox).
 * Cost is pre-computed by the Agent SDK and is authoritative.
 * Cache fields are present when the underlying Anthropic API call used prompt caching.
 * modelUsage carries the per-model token breakdown when the agent used multiple models.
 */
export interface AnthropicAgentUsage extends BaseTokenUsage {
  provider: "anthropic-agent";
  /**
   * Authoritative total cost in USD from the Agent SDK.
   * Absent on abort paths where the SDK never emits a final result IPC.
   */
  totalCostUsd?: number;
  /** Tokens served from Anthropic's prompt cache during the agent run */
  cacheReadInputTokens?: number;
  /** Tokens written to Anthropic's prompt cache during the agent run */
  cacheCreationInputTokens?: number;
  /** Per-model token and cost breakdown from the Agent SDK */
  modelUsage?: Readonly<
    Record<
      string,
      {
        readonly inputTokens: number;
        readonly outputTokens: number;
        readonly cacheReadInputTokens?: number;
        readonly cacheCreationInputTokens?: number;
        readonly costUSD: number;
        readonly webSearchRequests?: number;
        readonly contextWindow?: number;
        readonly maxOutputTokens?: number;
      }
    >
  >;
}

/**
 * Discriminated union of provider-specific token usage.
 * The `provider` field determines which cache fields are present and how cost is computed.
 */
export type LLMTokenUsage =
  | OpenAITokenUsage
  | AnthropicTokenUsage
  | GoogleTokenUsage
  | AnthropicAgentUsage;

// =============================================================================
// Billing constants
// =============================================================================

/** Prices are quoted per 1M tokens — divide by this to get per-token cost in USD */
const TOKEN_UNIT_SIZE = 1_000_000;

/** Billing system stores costs at 1/10,000th of a dollar precision (e.g., 25_000 = $2.50) */
const BILLING_PRECISION = 10_000;

/**
 * Billing multiplier applied after cost computation.
 * Set to 1.0 (no markup). Adjust to add platform margin (e.g., 1.5 = 50% markup).
 */
export const BILLING_MULTIPLIER = 1.0;

/**
 * How far off a whole unit the scaled cost may sit and still BE that unit.
 *
 * Relative rather than absolute, because a double's spacing grows with
 * magnitude: the residue on `0.0051 * 10_000` is ~1e-14, and on a cost a
 * million times larger it is a million times larger too. An absolute cut
 * cleans the small case and silently misses the large one.
 *
 * Far below any partial unit a caller can produce. The smallest non-zero
 * contribution in the catalog is one token at its cheapest rate — a cache read
 * at $0.005/1M, i.e. 5e-5 billing units — which is four to five orders of
 * magnitude above this tolerance, so no genuine fraction is ever absorbed.
 */
const FLOAT_RESIDUE_TOLERANCE = 1e-9;

/**
 * A raw-USD cost in billing units: ten-thousandths of a dollar, the billing
 * multiplier applied, rounded up so a partial unit is never under-charged.
 *
 * The scaled cost is snapped to a whole unit before rounding up. `0.0051 *
 * 10_000` is `51.00000000000001`, not `51`, so `Math.ceil` on its own turns a
 * double's rounding residue into a whole extra unit charged to the customer.
 * That is an over-charge on ordinary traffic rather than an edge case: 1,700
 * input tokens at $3/1M cost exactly $0.0051 and billed 52 units instead of
 * 51. Only residue is absorbed — a real partial unit still rounds up, so the
 * under-charge the ceiling exists to prevent is unaffected.
 */
export function billingUnitsFromUsd(costInUsd: number): number {
  const scaled = costInUsd * BILLING_PRECISION * BILLING_MULTIPLIER;
  const wholeUnits = Math.round(scaled);
  return Math.abs(scaled - wholeUnits) <= Math.abs(scaled) * FLOAT_RESIDUE_TOLERANCE
    ? wholeUnits
    : Math.ceil(scaled);
}

// =============================================================================
// Billing functions
// =============================================================================

/**
 * The cost of a generation, together with whether a price could be established
 * at all.
 *
 * `pricingResolved: false` means NO price was found and `costInUSDTenThousandths`
 * is a placeholder zero, NOT a measurement. The two are separate fields because
 * no in-band value of the cost can carry "unknown": SQL `SUM` ignores NULL, so
 * `SUM({100, NULL})` and `SUM({100, 0})` are both `100`. A total therefore
 * absorbs an unpriced call identically whether the writer stores `0` or stores
 * nothing, and only an out-of-band carrier makes the gap visible.
 */
export interface PricingOutcome {
  /** Cost in ten-thousandths of a dollar (e.g., 1500 = $0.15). */
  readonly costInUSDTenThousandths: number;
  /**
   * The same cost in dollars, BEFORE `billingUnitsFromUsd` rounds it up into a
   * whole billing unit — the figure `LLMGeneration.costUsd` stores.
   *
   * 🔴 THIS IS NOT `costInUSDTenThousandths / 10000`, AND DERIVING IT THAT WAY
   * IS THE WHOLE DEFECT THIS FIELD EXISTS TO REMOVE. The billing unit is a
   * ceiling, so every call under $0.0001 is recorded AS $0.0001: a measured
   * 460-token call truly costing $0.00001932 billed `1`, an overstatement of
   * 5.176x. Dividing that `1` back by 10,000 reproduces the inflated figure
   * exactly, and storing it in an exact column makes a ceiled number look like
   * a measurement. It must come from the pre-rounding arithmetic, which is why
   * this travels out of the same computation rather than being recovered by a
   * caller.
   *
   * `null` when, and only when, `pricingResolved` is `false` — no price was
   * established, so there is no exact cost either and the column stays NULL
   * rather than carrying the placeholder zero.
   *
   * Exact to what double arithmetic produced: the catalog's own rates are
   * doubles, so this is the true product of those rates and the token counts,
   * not an arbitrary-precision result. That is a ~15-significant-digit bound,
   * far below the 12 decimal places the column stores, and it is the honest
   * ceiling on this value's accuracy. The rounding it removes is the ceiling
   * above, which is four orders of magnitude larger.
   */
  readonly costUsd: number | null;
  /**
   * `true` when a price was established — from the pricing table, or from an
   * SDK-authoritative figure. `false` when none was, in which case the cost
   * above is a placeholder zero that must be excluded from any claim about spend.
   */
  readonly pricingResolved: boolean;
}

/**
 * The per-rate multipliers a long-context rule applies to one request's
 * usage — `{1, 1, 1}` when the request carries no rule, or sits at or under
 * `thresholdTokens` (strictly greater than triggers the rule, so a request
 * landing exactly on the threshold is not repriced).
 */
export function longContextFactors(
  rule: LongContextPricing | undefined,
  totalInputTokens: number
): { readonly input: number; readonly cache: number; readonly output: number } {
  if (!rule || totalInputTokens <= rule.thresholdTokens) {
    return { input: 1, cache: 1, output: 1 };
  }
  return {
    input: rule.inputMultiplier,
    cache: rule.cacheMultiplier,
    output: rule.outputMultiplier
  };
}

/**
 * The raw-USD cost of a usage object at pricing-table rates, before any
 * rounding into billing units — never reads `totalCostUsd`.
 *
 * Pricing semantics per provider:
 * - OpenAI: promptTokens is TOTAL input (includes cachedTokens and
 *   cacheWriteTokens, both subsets, disjoint from each other). The ordinary
 *   portion is what's left after removing both:
 *   `promptTokens - cachedTokens - (cacheWriteTokens ?? 0)`, per OpenAI's own
 *   worked example — cache-write pricing is not an additive fee, it replaces
 *   the input rate for the tokens it covers.
 * - Anthropic / anthropic-agent: promptTokens is NON-CACHE input only. Cache
 *   read and cache-creation fields are separate and additive.
 * - Google: promptTokens is TOTAL input (includes cached). Same as OpenAI's
 *   non-write semantics.
 *
 * When the pricing carries a `longContextPricing` rule and this request's
 * total input tokens exceed its threshold, every rate below is the
 * catalog rate times that rule's factor — computed once, up front, so the
 * per-shape arithmetic below never has to know the rule exists. Each cache
 * rate's `?? inputTokenPrice` fallback is resolved against the UN-multiplied
 * input price and only then multiplied by the cache factor, so a model with
 * no separate cache rate still has the long-context cache multiplier applied
 * to what it bills those tokens at.
 */
export function usdFromUsage(pricing: ModelPricing, usage: LLMTokenUsage): number {
  const factors = longContextFactors(pricing.longContextPricing, getTotalInputTokens(usage));
  const inputTokenPrice = pricing.inputTokenPrice * factors.input;
  const outputTokenPrice = pricing.outputTokenPrice * factors.output;
  const cacheReadTokenPrice =
    (pricing.cacheReadTokenPrice ?? pricing.inputTokenPrice) * factors.cache;
  const cacheWriteTokenPrice =
    (pricing.cacheWriteTokenPrice ?? pricing.inputTokenPrice) * factors.cache;

  switch (usage.provider) {
    case "openai": {
      const cacheWriteTokens = usage.cacheWriteTokens ?? 0;
      const ordinaryTokens = Math.max(
        0,
        usage.promptTokens - usage.cachedTokens - cacheWriteTokens
      );
      const inputCost =
        ordinaryTokens * inputTokenPrice +
        usage.cachedTokens * cacheReadTokenPrice +
        cacheWriteTokens * cacheWriteTokenPrice;
      return (inputCost + usage.completionTokens * outputTokenPrice) / TOKEN_UNIT_SIZE;
    }
    case "anthropic":
      return usdFromTokens(
        { inputTokenPrice, outputTokenPrice, cacheReadTokenPrice, cacheWriteTokenPrice },
        usage
      );
    case "anthropic-agent":
      return usdFromTokens(
        { inputTokenPrice, outputTokenPrice, cacheReadTokenPrice, cacheWriteTokenPrice },
        {
          promptTokens: usage.promptTokens,
          completionTokens: usage.completionTokens,
          cacheReadInputTokens: usage.cacheReadInputTokens,
          cacheCreationInputTokens: usage.cacheCreationInputTokens
        }
      );
    case "google": {
      const nonCachedTokens = Math.max(0, usage.promptTokens - usage.cachedTokens);
      const inputCost =
        nonCachedTokens * inputTokenPrice + usage.cachedTokens * cacheReadTokenPrice;
      return (inputCost + usage.completionTokens * outputTokenPrice) / TOKEN_UNIT_SIZE;
    }
    default: {
      const _exhaustive: never = usage;
      return _exhaustive;
    }
  }
}

/**
 * Compute billable cost from a model entry and the usage of EVERY vendor
 * request a task made, and report whether a price was actually found.
 *
 * Each request is priced on its own — {@link usdFromUsage} sees only that
 * request's own total input tokens, so a long-context rule is applied per
 * request and never to the sum of several. Only the final total is rounded
 * into billing units, via {@link billingUnitsFromUsd}, so a chunk that costs
 * a fraction of a unit is never independently rounded away.
 *
 * An `anthropic-agent` entry carrying an SDK-reported `totalCostUsd` folds
 * that figure straight in, unpriced by the catalog — the SDK figure is
 * authoritative and needs no catalog row, and it is what makes the list
 * "unpriced" only when it is EMPTY or when `pricing` is null and nothing in
 * it carries an SDK cost. Every other shape's arithmetic lives in
 * {@link usdFromUsage}, called once per request below.
 *
 * @param pricing - Model entry with pricing fields, or null if unknown
 * @param usages  - One usage object per vendor request the task made
 */
export function priceRequests(
  pricing: ModelPricing | null,
  usages: readonly LLMTokenUsage[]
): PricingOutcome {
  /**
   * No price exists. The zero is a placeholder, never a measurement, and
   * `costUsd` is null rather than that placeholder: an exact column carrying
   * 0 would read as a call that genuinely cost nothing.
   */
  const unpriced: PricingOutcome = {
    costInUSDTenThousandths: 0,
    costUsd: null,
    pricingResolved: false
  };

  if (usages.length === 0) {
    return unpriced;
  }

  // SDK cost is authoritative when present — including $0 (e.g., a fully
  // cached turn). Only fall back to the pricing table when the SDK did not
  // report cost at all (older SDK, edge cases). Without this fallback, the
  // tracing DB records $0 while the billing middleware records the real
  // token-based cost — a silent asymmetry between the two writers.
  const sdkCostOf = (usage: LLMTokenUsage): number | undefined =>
    usage.provider === "anthropic-agent" ? usage.totalCostUsd : undefined;

  if (!pricing && !usages.some((usage) => sdkCostOf(usage) !== undefined)) {
    return unpriced;
  }

  const totalUsd = usages.reduce((sum, usage) => {
    const sdkCost = sdkCostOf(usage);
    if (sdkCost !== undefined) {
      return sum + sdkCost;
    }
    // `pricing` is non-null here whenever this branch is reached without an
    // SDK cost: the guard above already returned when both were absent.
    if (!pricing) {
      return sum;
    }
    return sum + usdFromUsage(pricing, usage);
  }, 0);

  // Both scales from ONE figure. `totalUsd` is the summed pre-rounding
  // arithmetic, so the exact value is never back-derived from the ceiled
  // billing unit and the two cannot disagree about the same requests.
  return {
    costInUSDTenThousandths: billingUnitsFromUsd(totalUsd),
    costUsd: totalUsd,
    pricingResolved: true
  };
}

/**
 * Compute billable cost from a model entry and provider usage, and report
 * whether a price was actually found.
 *
 * **`pricing === null` is NOT the predicate for "unpriced", which is why this
 * function exists rather than the caller testing that itself.** An
 * `anthropic-agent` turn carrying an SDK-reported `totalCostUsd` is fully
 * priced with `pricing === null`, because the SDK figure is authoritative and
 * needs no catalog row. A caller that read `pricing === null` as "unpriced"
 * would flag every agent turn on a model absent from the catalog, including the
 * ones whose cost is exact.
 *
 * Behaves exactly as {@link priceRequests} called with a single-element list —
 * this is that call, kept as its own name for the single-request callers that
 * outnumber the chunked ones.
 *
 * @param pricing - Model entry with pricing fields, or null if unknown
 * @param usage   - Provider-specific token usage with cache fields
 */
export function priceWithResolution(
  pricing: ModelPricing | null,
  usage: LLMTokenUsage
): PricingOutcome {
  return priceRequests(pricing, [usage]);
}

/**
 * Compute billable cost from a model entry and provider usage.
 *
 * Returns `0` when no price could be established, which is indistinguishable
 * from a genuinely free call. Use {@link priceWithResolution} instead wherever
 * the difference matters — anything that persists a cost, or reports a spend
 * total. This wrapper stays for callers that only need the number.
 *
 * @param pricing - Model entry with pricing fields, or null if unknown
 * @param usage   - Provider-specific token usage with cache fields
 * @returns Cost in ten-thousandths of a dollar (e.g., 1500 = $0.15), or 0 if unknown
 */
export function computePriceFromPricing(
  pricing: ModelPricing | null,
  usage: LLMTokenUsage
): number {
  return priceWithResolution(pricing, usage).costInUSDTenThousandths;
}

/**
 * The raw-USD cost of token counts at pricing-table rates, before any rounding
 * into billing units — the scale a session's running cost total is kept in.
 */
export function usdFromTokens(
  pricing: Pick<
    ModelCatalogEntry,
    "inputTokenPrice" | "outputTokenPrice" | "cacheReadTokenPrice" | "cacheWriteTokenPrice"
  >,
  tokens: {
    readonly promptTokens: number;
    readonly completionTokens: number;
    readonly cacheReadInputTokens?: number;
    readonly cacheCreationInputTokens?: number;
  }
): number {
  const inputCost =
    tokens.promptTokens * pricing.inputTokenPrice +
    (tokens.cacheReadInputTokens ?? 0) * (pricing.cacheReadTokenPrice ?? pricing.inputTokenPrice) +
    (tokens.cacheCreationInputTokens ?? 0) *
      (pricing.cacheWriteTokenPrice ?? pricing.inputTokenPrice);
  return (inputCost + tokens.completionTokens * pricing.outputTokenPrice) / TOKEN_UNIT_SIZE;
}

/**
 * Compute an approximate cost from raw token counts × pricing-table rates.
 *
 * Use this when you have tokens but no precomputed SDK cost — e.g., mid-stream
 * partial upserts, or the abort path where the Agent SDK never emitted a final
 * result IPC. This always uses the pricing-table path; it does NOT read
 * `totalCostUsd` from any SDK source.
 *
 * For the clean-end path where an SDK-authoritative `totalCostUsd` is available,
 * continue to use `computePriceFromPricing` with the `anthropic-agent` variant
 * so the SDK's value is used directly.
 */
export function computePriceFromTokens(
  pricing: Parameters<typeof usdFromTokens>[0],
  tokens: Parameters<typeof usdFromTokens>[1]
): number {
  return billingUnitsFromUsd(usdFromTokens(pricing, tokens));
}

/**
 * Extract the total input token count from provider-specific usage.
 * Used for DB storage where we store a single `inputTokens` column.
 *
 * - OpenAI/Google: promptTokens is already the total — it includes cachedTokens
 *   and, for OpenAI, cacheWriteTokens too (both are subsets of promptTokens,
 *   disjoint from each other).
 * - Anthropic/anthropic-agent: promptTokens is the non-cache portion.
 *   Total = promptTokens + cacheRead + cacheCreation.
 *   Cache fields on anthropic-agent are optional — absent means no caching occurred.
 */
export function getTotalInputTokens(usage: LLMTokenUsage): number {
  if (usage.provider === "anthropic") {
    return usage.promptTokens + usage.cacheReadInputTokens + usage.cacheCreationInputTokens;
  }
  if (usage.provider === "anthropic-agent") {
    return (
      usage.promptTokens + (usage.cacheReadInputTokens ?? 0) + (usage.cacheCreationInputTokens ?? 0)
    );
  }
  return usage.promptTokens;
}

/**
 * Extract the cache-read token count from provider-specific usage, for the single
 * `cacheReadInputTokens` DB column. `undefined` means "this provider reported no cache
 * information", which is not the same as a measured zero.
 *
 * Each provider names the same quantity differently — Anthropic `cacheReadInputTokens`,
 * OpenAI and Google `cachedTokens` — and the mapping lived inline at each write site.
 * That duplication is exactly why Google's cache reads were captured by the provider
 * services, counted into `inputTokens` by `getTotalInputTokens`, and then silently
 * dropped on persistence: the writers had branches for Anthropic and OpenAI only, and
 * a third provider had to be remembered in two places at once. It is one place now.
 *
 * **The invariant every caller depends on: the result is always a SUBSET of
 * `getTotalInputTokens(usage)` for the same usage object.** Anthropic's cache read is
 * added into that total explicitly; OpenAI's and Google's `cachedTokens` are already
 * inside their `promptTokens`. That containment is what makes a cache hit rate a rate
 * rather than an arbitrary quotient — do not add a provider here without checking it.
 */
export function getCacheReadInputTokens(usage: LLMTokenUsage): number | undefined {
  if (usage.provider === "anthropic") return usage.cacheReadInputTokens;
  if (usage.provider === "anthropic-agent") return usage.cacheReadInputTokens;
  // OpenAI and Google both report a cached subset of promptTokens under one name.
  return usage.cachedTokens;
}

/**
 * Extract the cache-WRITE token count, for the `cacheCreationInputTokens` column.
 *
 * Anthropic always bills a separate cache-creation quantity. OpenAI's shape carries
 * one only where the reply reported it; usage from a reply that did not — a custom
 * endpoint, Kimi, JEV — carries `undefined` here, which is the honest answer: writing
 * 0 would assert the vendor measured zero writes, a claim it never made. Google's cache is populated implicitly and reports no write-side
 * count at all, so it is always `undefined`.
 *
 * The result is always a SUBSET of `getTotalInputTokens(usage)` for the same usage
 * object — OpenAI's `cacheWriteTokens` sits inside `promptTokens`, same as its
 * `cachedTokens` sibling.
 */
export function getCacheCreationInputTokens(usage: LLMTokenUsage): number | undefined {
  if (usage.provider === "anthropic") return usage.cacheCreationInputTokens;
  if (usage.provider === "anthropic-agent") return usage.cacheCreationInputTokens;
  if (usage.provider === "openai") return usage.cacheWriteTokens;
  return undefined;
}

/**
 * Sum two usage objects from the same provider (e.g. across chunks).
 * Throws if providers don't match.
 */
export function addUsage(a: LLMTokenUsage, b: LLMTokenUsage): LLMTokenUsage {
  if (a.provider === "openai" && b.provider === "openai") {
    return {
      provider: "openai",
      promptTokens: a.promptTokens + b.promptTokens,
      completionTokens: a.completionTokens + b.completionTokens,
      cachedTokens: a.cachedTokens + b.cachedTokens,
      cacheWriteTokens:
        a.cacheWriteTokens === undefined && b.cacheWriteTokens === undefined
          ? undefined
          : (a.cacheWriteTokens ?? 0) + (b.cacheWriteTokens ?? 0)
    };
  }
  if (a.provider === "anthropic" && b.provider === "anthropic") {
    return {
      provider: "anthropic",
      promptTokens: a.promptTokens + b.promptTokens,
      completionTokens: a.completionTokens + b.completionTokens,
      cacheReadInputTokens: a.cacheReadInputTokens + b.cacheReadInputTokens,
      cacheCreationInputTokens: a.cacheCreationInputTokens + b.cacheCreationInputTokens
    };
  }
  if (a.provider === "google" && b.provider === "google") {
    return {
      provider: "google",
      promptTokens: a.promptTokens + b.promptTokens,
      completionTokens: a.completionTokens + b.completionTokens,
      cachedTokens: a.cachedTokens + b.cachedTokens
    };
  }
  if (a.provider === "anthropic-agent" && b.provider === "anthropic-agent") {
    return {
      provider: "anthropic-agent",
      promptTokens: a.promptTokens + b.promptTokens,
      completionTokens: a.completionTokens + b.completionTokens,
      totalCostUsd: (a.totalCostUsd ?? 0) + (b.totalCostUsd ?? 0)
    };
  }
  throw new Error(`Cannot add usage from different providers: ${a.provider} vs ${b.provider}`);
}

// =============================================================================
// Legacy compat — ModelPricing shape (used by ModelPricingPort)
// =============================================================================

/** Pricing fields extracted from a catalog entry. Used by ModelPricingPort. */
export interface ModelPricing {
  inputTokenPrice: number;
  outputTokenPrice: number;
  cacheReadTokenPrice?: number;
  cacheWriteTokenPrice?: number;
  longContextPricing?: LongContextPricing;
}

/**
 * The only producer of a `ModelPricing` — every site that builds one from a
 * catalog entry goes through here, so a field added to `ModelPricing` has one
 * place to wire in rather than N call sites that silently drop it.
 */
export function pricingOf(entry: ModelPricing): ModelPricing {
  return {
    inputTokenPrice: entry.inputTokenPrice,
    outputTokenPrice: entry.outputTokenPrice,
    cacheReadTokenPrice: entry.cacheReadTokenPrice,
    cacheWriteTokenPrice: entry.cacheWriteTokenPrice,
    ...(entry.longContextPricing !== undefined && { longContextPricing: entry.longContextPricing })
  };
}

/** Look up model pricing by model ID. Returns null if not found in catalog. */
export function getModelPricing(modelId: string): ModelPricing | null {
  const entry = MODEL_CATALOG.get(modelId);
  if (!entry) return null;
  return pricingOf(entry);
}

/** Union of all model IDs present in the catalog. */
export type PricingModelId = string;

/** Type-safe union of all catalog model IDs (keys of models.json). */
export type CatalogModelId = keyof typeof modelsJson;

const catalogModelIds = Object.keys(modelsJson) as [CatalogModelId, ...CatalogModelId[]];

export const CatalogModelIdSchema = z.enum(catalogModelIds);

export const isCatalogModelId = (value: unknown): value is CatalogModelId =>
  CatalogModelIdSchema.safeParse(value).success;
